/* eslint-disable @typescript-eslint/no-explicit-any */
import { useLang } from 'hooks/lang';

import { FullRecruitment, recruitmentStatusConfig, loaders, getSampleProjectUrl } from 'entities';

import { Button, Card, Column, IconButton, Row, Table } from 'components/Blocks';
import { ChatIcon, RefreshIcon, ExpandIcon } from 'components/Icons';
import { DateSlice } from 'components/DateSlice';
import { UserAvatar } from 'components/UserAvatar';
import { EntityStatusTag } from 'components/Entities';

import { RecruitmentModal, RecruitmentOwner } from 'pages/Recruitment/components';

import { useRecruitmentHome } from './context';

import { listGet, listPartialUpdate, listRemove, listUpdate } from 'utils/array';
import { pick } from 'utils/object';
import { getLocalDateString } from 'utils/date';
import { truncateText } from 'utils/text';
import { getCountryLabel } from 'utils/countries';

const statusToConfig = {
  [recruitmentStatusConfig.active.code]: {
    requireDate: false,
    title: 'active-recruitments',
  },

  [recruitmentStatusConfig.notStarted.code]: {
    requireDate: false,
    title: 'not-started-recruitments',
  },

  [recruitmentStatusConfig.unassigned.code]: {
    requireDate: false,
    title: 'unassigned-recruitments',
  },

  [recruitmentStatusConfig.done.code]: {
    requireDate: true,
    title: 'done-recruitments',
  },

  [recruitmentStatusConfig.denied.code]: {
    requireDate: true,
    title: 'denied-recruitments',
  },
};

export function RecruitmentList() {
  const { t } = useLang();

  const { state, dispatch, changeRecruitmentFocus, changeStatus } = useRecruitmentHome();

  const { requireDate, title } = statusToConfig[state.status];

  const recruitments = loaders.projects.listRecruitmentsByStatus.use({
    reloadOnChangeRef: [
      state.status,
      state.endDate.toISOString(),
      state.startDate.toISOString(),
    ].join(),

    params: {
      status: state.status,

      ...(requireDate
        ? Object.fromEntries(
            Object.entries(pick(state, ['endDate', 'startDate'])).map(([key, date]) => [
              key,
              getLocalDateString(date as Date, { accountTimezone: true }),
            ]),
          )
        : {}),
    },

    defaultValue: [],

    refreshMinutes: 5,
  });

  const onRecruitment = (newData: Omit<FullRecruitment, 'requestedBy'>): void => {
    if (newData.status !== state.status) changeStatus(newData.status);

    recruitments.overwrite((old) => {
      const current = listGet(old, newData.id);

      if (!current) return old;

      return current.status !== newData.status
        ? listRemove(old, current)
        : listPartialUpdate(old, pick(newData, ['owner', 'commentCount']));
    });
  };

  return (
    <>
      {state.recruitmentId && (
        <RecruitmentModal
          recruitmentId={state.recruitmentId}
          close={() => changeRecruitmentFocus(null)}
          onData={onRecruitment}
        />
      )}

      <Column gap="6">
        <Row
          justifyContent="space-between"
          className="phone:flex-col phone:items-start phone:gap-1"
        >
          <h6 className="text-2xl text-primary">{t(title)}</h6>

          <Row gap="2" className="phone:justify-between">
            {requireDate && <DateSlice {...state} dispatch={dispatch} />}

            <IconButton onClick={recruitments.retry}>
              <RefreshIcon size={14} />
            </IconButton>
          </Row>
        </Row>

        <Card>
          <Table
            items={recruitments.data.filter(({ status }) => status === state.status)}
            loading={recruitments.loading}
            uniqueKey="id"
            headerClassName="pb-3"
            columns={[
              {
                label: 'Sample ID',
                property: 'sampleId',
                filter: true,
                className: 'w-20',

                render: ({ sampleId }) => (
                  <Button
                    size="fit"
                    variant="link"
                    className="text-sm font-normal text-inherit"
                    href={getSampleProjectUrl(sampleId, 'stats')}
                    linkTarget={sampleId}
                  >
                    {sampleId}
                  </Button>
                ),
              },

              {
                label: t('project')!,

                search: true,
                sort: true,

                getTextValue: ({ project }) => project.name,

                render: ({ project }) => (
                  <Button
                    size="fit"
                    variant="link"
                    to="projectProfile"
                    className="text-sm font-normal text-inherit"
                    params={{ projectId: project.id }}
                    tooltipContent={project.name.length > 35 ? project.name : null}
                  >
                    {truncateText(project.name, 35)}
                  </Button>
                ),
              },

              {
                label: t('country')!,
                property: 'country',
                filter: true,
                sort: true,
                getTextValue: ({ country }) => getCountryLabel(country),
              },

              {
                label: t('client')!,
                filter: true,
                sort: true,
                render: ({ project }) => project.clientName,
              },

              {
                label: t('owner')!,
                headerClassName: 'text-center',
                filter: true,

                getTextValue: ({ owner }) => owner.name,

                render: (recruitment) => (
                  <RecruitmentOwner
                    size={32}
                    recruitment={recruitment}
                    updateRecruitment={(rec) =>
                      recruitments.overwrite((old) => listUpdate(old, rec))
                    }
                  />
                ),
              },

              {
                label: t('requested-by')!,
                headerClassName: 'text-center',
                filter: true,

                getTextValue: ({ requestedBy }) => requestedBy.name,

                render: ({ requestedBy }) => (
                  <Row justifyContent="center">
                    <UserAvatar size={32} user={requestedBy} name />
                  </Row>
                ),
              },

              {
                label: t('status') as string,

                render: ({ status }) => (
                  <EntityStatusTag target="recruitments" status={status} size="medium" />
                ),
              },

              {
                className: 'w-[200px]',

                render: (recruitment) => {
                  return (
                    <Row justifyContent="flex-end">
                      <span className="flex items-center gap-2 text-sm">
                        {recruitment.commentCount} <ChatIcon size={20} />
                      </span>

                      <IconButton onClick={() => changeRecruitmentFocus(recruitment.id)}>
                        <ExpandIcon size={20} />
                      </IconButton>
                    </Row>
                  );
                },
              },
            ]}
          />
        </Card>
      </Column>
    </>
  );
}
